import { Download } from '@mui/icons-material';
import { Alert } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { EiaNaturalGasStorageDataSource, EiaNaturalGasStorageWeekModel } from '../../../../Generated/Commodity-Management-Api';
import { MarketViewApplication } from '../../../../Generated/Mark-It-View-Api';
import { useLoadingState } from '../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { UserAccessState } from '../../../../Redux/Slices/UserAccessSlice';
import { StoneXLoading, StoneXRow } from '../../../StoneX';
import StoneXMainPage from '../../../StoneX/StoneXMainPage/StoneXMainPage';
import { StoneXButton, StoneXMultiselect, StoneXSelect, StoneXTextField, StoneXToggleButton } from '../../../StoneXMui';
import EiaGasStorageChart from './EiaGasStorageChart';
import {
  availableSeasons,
  buildStorageTable,
  fiveYearStatistics,
  regionPoints,
  seasonLabel,
  seasonSeries,
  STORAGE_REGION_LABELS,
  STORAGE_REGIONS,
  StorageRegion,
} from './EiaGasStorageLogic';
import EiaGasStorageTable from './EiaGasStorageTable';
import { exportEiaGasStorageToExcel } from './exportEiaGasStorageToExcel';

const DEFAULT_SEASON_COUNT = 2;

function parseScale(value: string): number | undefined {
  const parsed = Number(value.trim());
  return value.trim() === '' || Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * Working natural gas in underground storage by storage season, from the EIA Weekly Natural Gas
 * Storage Report, with the five year average and range. Replaces the WebTools gas storage page.
 */
export default function EiaGasStorageTool() {
  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);
  const canOpen = userAccess.isAdmin || userAccess.marketViewApplications.includes(MarketViewApplication.EiaNaturalGasStorage);

  const weeksLoadingState = useLoadingState();
  const [weeks, setWeeks] = useState<EiaNaturalGasStorageWeekModel[]>([]);
  const [dataSource, setDataSource] = useState<EiaNaturalGasStorageDataSource | undefined>();

  const [region, setRegion] = useState<StorageRegion>('Total');
  // Null until the user picks seasons, so the most recent ones are shown once the data arrives.
  const [chosenSeasons, setChosenSeasons] = useState<number[] | null>(null);
  const [showAverage, setShowAverage] = useState<boolean>(true);
  const [showRange, setShowRange] = useState<boolean>(true);
  const [topScale, setTopScale] = useState<string>('');
  const [bottomScale, setBottomScale] = useState<string>('');
  const [scaleStep, setScaleStep] = useState<string>('');
  const [exporting, setExporting] = useState<boolean>(false);

  useEffect(() => {
    if (!canOpen) return;
    CommodityManagementApi.listEiaNaturalGasStorageWeeks(weeksLoadingState.setLoadingState).then(response => {
      setWeeks(response.data.rows ?? []);
      setDataSource(response.data.dataSource);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canOpen]);

  const points = useMemo(() => regionPoints(weeks, region), [weeks, region]);
  const seasons = useMemo(() => availableSeasons(weeks, region), [weeks, region]);
  const activeSeasons = useMemo(() => {
    const chosen = chosenSeasons ?? seasons.slice(0, DEFAULT_SEASON_COUNT);
    return chosen.filter(season => seasons.includes(season)).sort((a, b) => b - a);
  }, [chosenSeasons, seasons]);

  const series = useMemo(() => seasonSeries(points, activeSeasons), [points, activeSeasons]);
  const fiveYear = useMemo(() => (activeSeasons.length > 0 ? fiveYearStatistics(points, activeSeasons[0]) : []), [points, activeSeasons]);
  const table = useMemo(() => buildStorageTable(points, activeSeasons), [points, activeSeasons]);

  const latestWeekEnding = points.length > 0 ? points[points.length - 1].weekEnding : undefined;
  const fromDatabricks = dataSource === EiaNaturalGasStorageDataSource.Databricks;
  const showFiveYear = showAverage || showRange;

  async function exportToExcel() {
    if (!table) return;
    setExporting(true);
    try {
      await exportEiaGasStorageToExcel({ region, table, includeFiveYear: showFiveYear, weeks, latestWeekEnding });
    } finally {
      setExporting(false);
    }
  }

  if (!canOpen) {
    return <Navigate to="/" replace />;
  }

  return (
    <StoneXMainPage>
      <StoneXRow justify="space-between">
        <h1>EIA Gas Storage</h1>
        <StoneXButton variant="outlined" startIcon={<Download />} onClick={exportToExcel} isWorking={exporting} disabled={!table}>
          Export to Excel
        </StoneXButton>
      </StoneXRow>

      <StoneXRow align="start">
        <StoneXSelect
          label="Region"
          options={STORAGE_REGIONS}
          getId={option => option}
          getOptionLabel={option => STORAGE_REGION_LABELS[option]}
          value={region}
          onChange={option => setRegion(option ?? 'Total')}
          isControlled
        />
        <StoneXMultiselect
          key={`${region}-${seasons.join(',')}`}
          label="Seasons (Nov - Oct)"
          list={seasons}
          labelSelector={(season: number) => seasonLabel(season)}
          valueSelector={(season: number) => season}
          initial={activeSeasons}
          resultSetter={(selected: number[]) => setChosenSeasons(selected)}
          sortComparator={(a, b) => b - a}
        />
        <StoneXToggleButton label="5-Year Average" value={showAverage} onChange={setShowAverage} />
        <StoneXToggleButton label="5-Year Range" value={showRange} onChange={setShowRange} />
        <StoneXTextField label="Top Scale" type="number" width="small" value={topScale} onUpdate={setTopScale} placeholder="Auto" />
        <StoneXTextField label="Bottom Scale" type="number" width="small" value={bottomScale} onUpdate={setBottomScale} placeholder="Auto" />
        <StoneXTextField label="Scale By" type="number" width="small" value={scaleStep} onUpdate={setScaleStep} placeholder="Auto" />
      </StoneXRow>

      <StoneXLoading show={weeksLoadingState.isLoading()} />

      {weeksLoadingState.failed() && <Alert severity="error">The storage history could not be loaded.</Alert>}

      {weeksLoadingState.isLoaded() && weeks.length === 0 && (
        <Alert severity="info">No storage weeks are available.</Alert>
      )}

      {weeksLoadingState.isLoaded() && latestWeekEnding && (
        <span style={{ color: '#616161' }}>
          Latest week ending {dayjs(latestWeekEnding).format('MMMM D, YYYY')}. Stocks in billion cubic feet. Source: EIA Weekly Natural Gas Storage Report
          {fromDatabricks ? ' via Databricks, updated weekly.' : ', stored copy.'}
        </span>
      )}

      {weeksLoadingState.isLoaded() && activeSeasons.length > 0 && table && (
        <>
          <EiaGasStorageChart
            region={region}
            seasons={series}
            fiveYear={fiveYear}
            showAverage={showAverage}
            showRange={showRange}
            latestWeekEnding={latestWeekEnding}
            yAxisMaximum={parseScale(topScale)}
            yAxisMinimum={parseScale(bottomScale)}
            yAxisStep={parseScale(scaleStep)}
          />
          <h3 style={{ marginBottom: 4 }}>
            {STORAGE_REGION_LABELS[region]} · Stocks in billion cubic feet (Bcf)
          </h3>
          <EiaGasStorageTable table={table} showFiveYear={showFiveYear} />
        </>
      )}

      {weeksLoadingState.isLoaded() && weeks.length > 0 && activeSeasons.length === 0 && (
        <Alert severity="info">Choose at least one season to see the chart and table.</Alert>
      )}

    </StoneXMainPage>
  );
}
